import { useState } from 'react';

// --- Vergi Hesaplama ---
function TaxCalculator() {
  const [gelir, setGelir] = useState('');
  const [gider, setGider] = useState('');
  const [tip, setTip] = useState('sahis');
  const [result, setResult] = useState<null | { matrah: number; vergi: number; net: number }>(null);

  const hesapla = () => {
    const g = parseFloat(gelir.replace(/\./g, '').replace(',', '.')) || 0;
    const gi = parseFloat(gider.replace(/\./g, '').replace(',', '.')) || 0;
    const matrah = Math.max(0, g - gi);
    let vergiOrani = 0;
    if (tip === 'sahis') {
      if (matrah <= 110000) vergiOrani = 0.15;
      else if (matrah <= 230000) vergiOrani = 0.20;
      else if (matrah <= 580000) vergiOrani = 0.27;
      else if (matrah <= 3000000) vergiOrani = 0.35;
      else vergiOrani = 0.40;
    } else {
      vergiOrani = 0.25;
    }
    const vergi = matrah * vergiOrani;
    setResult({ matrah, vergi, net: matrah - vergi });
  };

  const fmt = (n: number) =>
    n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="bg-[#0A2540] rounded-2xl p-7 flex flex-col h-full">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl">
          <i className="ri-calculator-line text-white text-lg"></i>
        </div>
        <div>
          <h3 className="text-white font-semibold text-base">Vergi Hesaplama Aracı</h3>
          <p className="text-white/50 text-xs">Yaklaşık vergi yükünüzü hesaplayın</p>
        </div>
      </div>

      <div className="space-y-3 flex-1">
        <div>
          <label className="text-white/60 text-xs mb-1.5 block">Yıllık Gelir (₺)</label>
          <input
            type="text"
            value={gelir}
            onChange={(e) => setGelir(e.target.value)}
            placeholder="örn. 500.000"
            className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors"
          />
        </div>
        <div>
          <label className="text-white/60 text-xs mb-1.5 block">Yıllık Gider (₺)</label>
          <input
            type="text"
            value={gider}
            onChange={(e) => setGider(e.target.value)}
            placeholder="örn. 150.000"
            className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors"
          />
        </div>
        <div>
          <label className="text-white/60 text-xs mb-1.5 block">Şirket Tipi</label>
          <select
            value={tip}
            onChange={(e) => setTip(e.target.value)}
            className="w-full bg-white/10 border border-white/15 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors cursor-pointer"
          >
            <option value="sahis" className="text-[#0A2540]">Şahıs Şirketi</option>
            <option value="limited" className="text-[#0A2540]">Limited / A.Ş.</option>
          </select>
        </div>
      </div>

      <button
        onClick={hesapla}
        className="mt-5 w-full py-3 bg-white text-[#0A2540] text-sm font-semibold rounded-lg hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
      >
        Hesapla
      </button>

      {result && (
        <div className="mt-4 p-4 bg-white/10 rounded-xl space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Vergi Matrahı</span>
            <span className="text-white font-medium">₺{fmt(result.matrah)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Tahmini Vergi</span>
            <span className="text-rose-400 font-semibold">₺{fmt(result.vergi)}</span>
          </div>
          <div className="flex justify-between text-sm border-t border-white/10 pt-2">
            <span className="text-white/60">Net Kazanç</span>
            <span className="text-emerald-400 font-semibold">₺{fmt(result.net)}</span>
          </div>
          <p className="text-white/30 text-xs pt-1">* Bu hesaplama yaklaşık değer içerir. Kesin bilgi için danışmanlık alınız.</p>
        </div>
      )}
    </div>
  );
}

// --- Beyanname Hatırlatma ---
function DeclarationReminder() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d7c0nm2qofe8vli40s3g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const dates = [
    { label: 'KDV Beyannamesi', date: '26 Nis', color: 'bg-blue-100 text-blue-700' },
    { label: 'Muhtasar Beyanname', date: '26 Nis', color: 'bg-emerald-100 text-emerald-700' },
    { label: 'SGK Bildirimi', date: '23 Nis', color: 'bg-amber-100 text-amber-700' },
    { label: 'Kurumlar Vergisi', date: '30 Nis', color: 'bg-rose-100 text-rose-700' },
  ];

  return (
    <div className="bg-white rounded-2xl p-6 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-[#F5F7FA] rounded-xl">
          <i className="ri-calendar-line text-[#0A2540] text-lg"></i>
        </div>
        <div>
          <h3 className="text-[#0A2540] font-semibold text-sm">Beyanname Hatırlatma</h3>
          <p className="text-gray-400 text-xs">Kritik tarihleri kaçırmayın</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {dates.map((d) => (
          <div key={d.label} className={`px-3 py-2 rounded-lg ${d.color}`}>
            <div className="text-xs font-semibold">{d.date}</div>
            <div className="text-xs opacity-80 leading-tight mt-0.5">{d.label}</div>
          </div>
        ))}
      </div>

      {sent ? (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
          <i className="ri-checkbox-circle-line text-emerald-600"></i>
          <span className="text-emerald-700 text-sm">Kaydedildi! Hatırlatmalar gönderilecek.</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-readdy-form className="flex gap-2">
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-posta adresiniz"
            required
            className="flex-1 min-w-0 border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#0A2540] placeholder-gray-400 focus:outline-none focus:border-[#0A2540] transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-[#0A2540] text-white text-sm font-medium rounded-lg hover:bg-[#0A2540]/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {loading ? '...' : 'Hatırlat'}
          </button>
        </form>
      )}
    </div>
  );
}

// --- Şirket Kurma Rehberi ---
function CompanyGuide() {
  const [step, setStep] = useState(0);
  const steps = [
    { title: 'Şirket Tipi Seçimi', desc: 'Şahıs mı, Limited mi, A.Ş. mi? İhtiyacınıza göre doğru yapıyı belirleyin.' },
    { title: 'Belge Hazırlığı', desc: 'Kimlik, adres, sermaye bilgileri ve ana sözleşme hazırlanır.' },
    { title: 'Ticaret Sicili', desc: 'Ticaret Sicil Müdürlüğü\'ne başvuru ve tescil işlemleri tamamlanır.' },
    { title: 'Vergi Kaydı', desc: 'Vergi dairesi kaydı, imza sirküleri ve levha işlemleri yapılır.' },
  ];

  return (
    <div className="bg-[#F5F7FA] rounded-2xl p-6 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl">
          <i className="ri-building-line text-[#0A2540] text-lg"></i>
        </div>
        <div>
          <h3 className="text-[#0A2540] font-semibold text-sm">Şirket Kurma Rehberi</h3>
          <p className="text-gray-400 text-xs">Adım adım süreç</p>
        </div>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-1">
        {steps.map((_, i) => (
          <div key={i} className="flex items-center gap-1 flex-1">
            <button
              onClick={() => setStep(i)}
              className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold transition-all cursor-pointer ${
                i === step ? 'bg-[#0A2540] text-white' : i < step ? 'bg-emerald-500 text-white' : 'bg-white text-gray-400'
              }`}
            >
              {i < step ? <i className="ri-check-line text-xs"></i> : i + 1}
            </button>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 ${i < step ? 'bg-emerald-500' : 'bg-gray-200'}`}></div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl p-4">
        <div className="text-[#0A2540] font-semibold text-sm mb-1">{steps[step].title}</div>
        <div className="text-gray-500 text-xs leading-relaxed">{steps[step].desc}</div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="flex-1 py-2 border border-gray-200 text-gray-500 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
        >
          Geri
        </button>
        <button
          onClick={() => setStep(Math.min(steps.length - 1, step + 1))}
          disabled={step === steps.length - 1}
          className="flex-1 py-2 bg-[#0A2540] text-white text-xs font-medium rounded-lg hover:bg-[#0A2540]/90 transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
        >
          İleri
        </button>
      </div>
    </div>
  );
}

// --- Gider/Kâr Simülasyonu ---
function ProfitSimulator() {
  const [ciro, setCiro] = useState('');
  const [giderOrani, setGiderOrani] = useState(40);
  const [result, setResult] = useState<null | { gider: number; kar: number; oran: number }>(null);

  const hesapla = () => {
    const c = parseFloat(ciro.replace(/\./g, '').replace(',', '.')) || 0;
    const gider = c * (giderOrani / 100);
    const kar = c - gider;
    setResult({ gider, kar, oran: giderOrani });
  };

  const fmt = (n: number) => n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });

  return (
    <div className="bg-white rounded-2xl p-6 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-[#F5F7FA] rounded-xl">
          <i className="ri-bar-chart-2-line text-[#0A2540] text-lg"></i>
        </div>
        <div>
          <h3 className="text-[#0A2540] font-semibold text-sm">Kâr Simülasyonu</h3>
          <p className="text-gray-400 text-xs">Finansal öngörü aracı</p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-gray-500 text-xs mb-1.5 block">Aylık Ciro (₺)</label>
          <input
            type="text"
            value={ciro}
            onChange={(e) => setCiro(e.target.value)}
            placeholder="örn. 100.000"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#0A2540] placeholder-gray-400 focus:outline-none focus:border-[#0A2540] transition-colors"
          />
        </div>
        <div>
          <div className="flex justify-between mb-1.5">
            <label className="text-gray-500 text-xs">Gider Oranı</label>
            <span className="text-[#0A2540] text-xs font-semibold">%{giderOrani}</span>
          </div>
          <input
            type="range"
            min={10}
            max={90}
            value={giderOrani}
            onChange={(e) => setGiderOrani(Number(e.target.value))}
            className="w-full accent-[#0A2540] cursor-pointer"
          />
        </div>
      </div>

      <button
        onClick={hesapla}
        className="w-full py-2.5 bg-[#0A2540] text-white text-sm font-medium rounded-lg hover:bg-[#0A2540]/90 transition-colors cursor-pointer whitespace-nowrap"
      >
        Simüle Et
      </button>

      {result && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-rose-100 rounded-lg p-2.5">
              <div className="text-rose-600 text-xs">Gider</div>
              <div className="text-rose-700 font-bold text-sm">₺{fmt(result.gider)}</div>
            </div>
            <div className="flex-1 bg-emerald-100 rounded-lg p-2.5">
              <div className="text-emerald-600 text-xs">Net Kâr</div>
              <div className="text-emerald-700 font-bold text-sm">₺{fmt(result.kar)}</div>
            </div>
          </div>
          {/* Bar */}
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${100 - result.oran}%` }}
            ></div>
          </div>
          <p className="text-gray-400 text-xs">Kâr marjı: %{100 - result.oran}</p>
        </div>
      )}
    </div>
  );
}

export default function Tools() {
  return (
    <section id="araclar" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
          <div>
            <span className="text-xs font-semibold tracking-widest text-[#60A5FA] uppercase">Dijital Araçlar</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mt-3">
              Size Özel Araçlar
            </h2>
          </div>
          <p className="text-gray-500 text-sm max-w-sm leading-relaxed">
            Mali süreçlerinizi kolaylaştıran, bilgilendirici dijital araçlar. Kesin sonuçlar için danışmanlık almanızı öneririz.
          </p>
        </div>

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left - Tax Calculator (tall) */}
          <div className="lg:row-span-2">
            <TaxCalculator />
          </div>

          {/* Right top */}
          <DeclarationReminder />

          {/* Right bottom - 2 cols */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <CompanyGuide />
            <ProfitSimulator />
          </div>
        </div>
      </div>
    </section>
  );
}
